import {BaseFeatureFields} from './useFeaturesQuery';
import {useEffect, useState} from 'react';
import {GetFeaturesResponse} from '../../services/api-types';
import {UseBaseFeature, useBaseFeature} from './useBaseFeature';
import {InPageAlertProps} from '@snsw-gel/in-page-alert/src/InPageAlert';

export interface UseAlertMessageCheck extends UseBaseFeature{
    readonly data?: {
        /** Whether alert messaging should be shown to the user */
        readonly displayAlertMessage: boolean;
        /** List of alert messages to be displayed  */
        alertMessaging: readonly {
            title: string;
            messages: readonly string[];
            variant: InPageAlertProps['variant']; // 'info', 'error', etc.
        }[];
    };
}

/**
 * Hook for managing a specific feature for Alert messages.
 * It checks if any alerts are active and provides a list of messages to be displayed to the user.
 * @returns Object containing methods to get current alert and loading state, which depends on the useFeaturesQuery hook.
 */
export function useAlertMessageCheck(): UseAlertMessageCheck {
    const featureToggle = useBaseFeature();

    const {isLoadingError, error, isFeatureQueryLoading, featureResponse} = featureToggle;
    const [result, setResult] = useState<UseAlertMessageCheck | null>(null);

    if (isLoadingError) {
        console.error('Failed during fetching of feature query', error);
    }

    useEffect(() => {
        const handleInterval = (isLoading: boolean, data?: GetFeaturesResponse) => {
            setResult(
                calculateAlertMessages(isLoading, data)
            );
        }

        // Check once at the start
        handleInterval(isFeatureQueryLoading, featureResponse);

        // Set up a continual check
        const interval = setInterval(handleInterval, ONE_MINUTE_IN_MILLIS, isFeatureQueryLoading, featureResponse);

        // Cleanup the interval when react deps change or component destruction
        return () => clearInterval(interval);
    }, [featureResponse, isFeatureQueryLoading]);

    const calculateAlertMessages = (isFeatureQueryLoading: boolean, data?: GetFeaturesResponse): UseAlertMessageCheck => {
        if (isFeatureQueryLoading) {
            return {
                isFeatureQueryLoading: true,
            } as UseAlertMessageCheck;
        }

        const alertMessageFeatures = data?.features
            .filter(({name}) => name.toLowerCase().startsWith('alertmessage'))
            .map(({fields}) => fields as AlertMessage)
            .filter(shouldAlertMessageBeDisplayed) ?? [];

        // Sort by priority
        const sortedAlerts = alertMessageFeatures.sort((a, b) => a.priority - b.priority);

        const alertMessaging = sortedAlerts.map(alert => ({
            title: alert.title,
            messages: alert.messages,
            variant: alert.messageVariant,
        }));

        return {
            data: {
                displayAlertMessage: alertMessaging.length > 0,
                alertMessaging,
            },
            ...featureToggle
        };
    }

    return result ?? {isFeatureQueryLoading: true} as UseAlertMessageCheck;
}

const ONE_MINUTE_IN_MILLIS = 60_000;

interface AlertMessage extends BaseFeatureFields {
    /**
     * The Alert to be displayed to the user.
     * Each list item corresponds to a paragraph.
     */
    readonly messages: readonly string[];
    /**
     * Priority of the alert message. Lower numbers indicate higher priority.
     * @example 100
     */
    readonly priority: number;
    /**
     * The variant/type of the alert message.
     * E.g., INFO, WARNING, ERROR, etc.
     */
    readonly messageVariant: string;
    /**
     * title of the message
     */
    readonly title: string;
}

function shouldAlertMessageBeDisplayed(alert: AlertMessage): boolean {
    // Minimum between start of date,
    const startTime = new Date(alert.startDateTime );
    const endTime = new Date(alert.endDateTime ?? '2099-01-01');
    const now = new Date();
    return startTime < now && now < endTime;
}
